// ./i18n/en.ts
import { TranslationMessages } from 'react-admin';
import defaultEnglishMessages from 'ra-language-english';

const customEnglishMessages: TranslationMessages = {
    ...defaultEnglishMessages,
    // Ensure that we correctly merge the `ra` namespace
    ra: {
        ...defaultEnglishMessages.ra,
        validation: {
            ...defaultEnglishMessages.ra.validation,
            // You can override the required message, or add other customizations:
            required: 'This field is required',
            // Optionally, override additional keys:
            // minLength: 'Must be at least %{min} characters',
            // maxLength: 'Must be %{max} characters or less',
            // etc.
        },
    },
    // Other custom keys:
    pos: {
        search: 'Search',
        configuration: 'Configuration',
        language: 'Language',
        theme: {
            name: 'Theme',
            light: 'Light',
            dark: 'Dark',
        },
        dialog: {
            synchronization: 'Confirm Synchronization',
            description: 'Are you sure you want to synchronize now?',
            cancel: 'Cancel',
            sync: 'Sync',
        },
        dashboard: {
            loading: 'Loading...',
            syncing: 'Syncing...',
            sync_now: 'Sync Now',
            monthly_revenue: 'Monthly Revenue',
            month_history: '30 Day Revenue History',
            new_orders: 'New Orders',
            pending_reviews: 'Pending Reviews',
            all_reviews: 'See all reviews',
            new_customers: 'New Customers',
            all_customers: 'See all customers',
            start_sync: 'Start sync',
            artists: 'Artists',
            categories: 'Categories',
            shows: 'Shows',
            last_sync: 'Last Sync',
            sync_message: "<p>Synchronization occurs on the <strong>server</strong> and may take time to load data from <strong>Airtable</strong>, the <strong>PodToo API</strong>, and the <strong>Insomniac API</strong> for the artists. All data is synchronized to <strong>Firebase</strong> using a unified, clearly structured format, ensuring consistency and optimal organization. The button indicates sync status—if it's disabled, synchronization is in progress. Multiple syncs can run simultaneously, and syncing will be available again after a few minutes.<br><br>Manual syncing isn't necessary, as it happens automatically every six hours at <strong>00:00, 06:00, 12:00, and 18:00 UTC</strong>.</p>",
            order: {
                items: 'by %{customer_name}, one item |||| by %{customer_name}, %{nb_items} items',
            },
            welcome: {
                title: 'Welcome to the react-admin e-commerce demo',
                subtitle:
                    "This is the admin of an imaginary poster shop. Feel free to explore and modify the data - it's local to your computer, and will reset each time you reload.",
                sync_log_button: 'View Sync Logs',
                demo_button: 'Source for this demo',
            },
        },
        menu: {
            sales: 'Sales',
            catalog: 'Catalog',
            customers: 'Customers',
        },
        events: {
            review: {
                title: 'Posted review on "%{product}"',
            },
            order: {
                title: 'Ordered 1 poster |||| Ordered %{smart_count} posters',
            },
        },
    },
    resources: {
        likes: {
            name: 'Likes',
        },
        handsup: {
            name: 'HandsUp',
        },
        voting: {
            name: 'Vote |||| Votes',
            empty: {
                title: 'No voting events yet',
                subtitle: 'Create your first voting event to get started',
                createButton: 'Create Voting Event',
            },
            fields: {
                vote_count: 'Vote Count',
                upvotes: 'Upvotes',
                downvotes: 'Downvotes',
                user: 'User',
                voted_by: 'Voted By',
                voted_at: 'Voted At',
                vote_type: 'Vote Type',
                net_votes: 'Net Votes',
            },
        },
        notifications: {
            name: 'Notifications',
        },
        messageTemplates: {
            name: 'Templates',
        },
        channels: {
            name: 'Channels',
        },
        channelHealth: {
            name: 'Channel Health',
        },
        lineupGame: {
            name: 'Lineup Game',
        },
        admins: {
            name: 'Admins',
        },
        shows: {
            name: 'Shows',
            fields: {
                title: 'Title',
                subtitle: 'Subtitle',
                description: 'Description',
                thumbnail: 'Thumbnail',
                episodes: 'Episodes',
                duration: 'Duration (seconds)',
            },
        },
        segments: {
            name: 'Likes',
            fields: {
                customers: 'Customers',
                name: 'Name',
            },
            data: {
                compulsive: 'Compulsive',
                collector: 'Collector',
                ordered_once: 'Ordered once',
                regular: 'Regular',
                returns: 'Returns',
                reviewer: 'Reviewer',
            },
        },
    },
    categories: {
        name: 'Categories'
    },
    notificationsPage: {
        notifications: 'Notifications',
        title: 'Title',
        message: 'Message',
        selectRecipients: 'Select Recipients',
        specificUsers: 'Specific Users',
        boradcastToAll: 'Broadcast to all users',
        selectUsers: 'Select Users',
        searchUsers: 'Search users...',
        sendNotification: 'Send Notification',
        errorLoadingUsers: 'Error loading users',
        confirmTitle: 'Confirm Notification',
        confirmDesc: 'Are you sure you want to send this push notification?',
        confirmDescSendAll: 'Are you sure you want to send this push notification to all users? This action cannot be undone.',
        confirm: 'Send',
        cancel: 'Cancel',
    },
    votingPage: {
        voting: 'Voting',
        upvote: 'Upvote',
        downvote: 'Downvote',
        removeVote: 'Remove Vote',
        votesPerUser: 'Votes Per User',
        voteSubmitted: 'Vote submitted successfully',
        voteRemoved: 'Vote removed successfully',
        voteError: 'Error submitting vote',
        alreadyVoted: 'You have already voted',
        mustLogin: 'You must be logged in to vote',
    },
    showsPage: {
        createShow: 'Create Show',
        editShow: 'Edit Show',
        showTitle: 'Show Title',
        subtitle: 'Subtitle (optional)',
        description: 'Description',
        showImage: 'Show Image',
        uploadImage: 'Upload Image',
        uploading: 'Uploading...',
        episodes: 'Episodes',
        episode: 'Episode',
        episodeTitle: 'Episode Title',
        episodeDescription: 'Episode Description (optional)',
        mediaSource: 'Media Source',
        videoUrl: 'Video URL',
        videoUpload: 'Video Upload',
        audioUrl: 'Audio URL',
        audioUpload: 'Audio Upload',
        uploadVideo: 'Upload Video',
        uploadAudio: 'Upload Audio',
        videoUrlHelper: 'e.g., https://youtube.com/watch?v=... or https://youtu.be/...',
        audioUrlHelper: 'Enter a direct audio URL (MP3, WAV, etc.)',
        durationHelper: 'Auto-detected from uploaded media — edit if incorrect',
        addEpisode: 'Add Episode',
        creating: 'Creating...',
        cancel: 'Cancel',
        save: 'Save',
        noShowsYet: 'No shows yet',
        infoMessage: 'Custom shows are protected from sync deletion and will automatically appear in the VIDEO HIGHLIGHTS category.',
        infoNote: 'Data will be visible in the app after the next sync cycle (up to 6 hours) or after a manual sync from the dashboard.',
        editInfoMessage: 'Changes will be reflected in the app after the next sync cycle (up to 6 hours) or you can trigger a manual sync from the dashboard.',
        showId: 'Show ID',
        mediaLabel: 'Media (Video URL, Video Upload, or Audio Upload)',
        categories: 'Categories',
        selectCategories: 'Select categories...',
        categoriesHelper: 'Optional: Select categories where this show should appear',
        createSuccess: 'Show created successfully',
        createError: 'Error creating show',
        updateSuccess: 'Show updated successfully',
        updateError: 'Error updating show',
        deleteConfirmTitle: 'Delete Show',
        deleteConfirmContent: 'Are you sure you want to delete this show? This action cannot be undone.',
        validationTitleRequired: 'Please enter a show title',
        validationImageRequired: 'Please upload a show image',
        validationEpisodeRequired: 'Please add at least one episode with a video URL, uploaded video, audio URL, or uploaded audio',
        videoUploadSuccess: 'Video uploaded successfully',
        audioUploadSuccess: 'Audio uploaded successfully',
        imageUploadSuccess: 'Image uploaded successfully',
        uploadError: 'Error uploading',
        invalidVideoFormat: 'Invalid video format. Only .mp4 files are allowed.',
        uploadInProgress: 'Please wait for the media upload to finish before saving.',
        episodeMissingMedia: 'Episode %{number} has no video or audio. Please add media or remove the episode before saving.',
    },
    channelsPage: {
        createChannel: 'Create Channel',
        editChannel: 'Edit Channel',
        title: 'Title',
        channelId: 'Channel ID',
        wave: 'Wave',
        streamUrl: 'Stream URL',
        description: 'Description',
        actionType: 'Action Type',
        handsUp: 'Hands Up',
        chat: 'Chat',
        disabled: 'Disabled',
        channelImage: 'Channel Image',
        createSuccess: 'Channel created successfully',
        createError: 'Error creating channel',
        updateSuccess: 'Channel updated successfully',
        updateError: 'Error updating channel',
    },
    channelHealthPage: {
        title: 'Channel Health',
        subtitle: 'Live monitoring of radio stream health. Checks run every 5 minutes.',
        noData: 'No health check data available yet. Data will appear after the first check runs.',
        responseTime: 'Response time',
        lastChecked: 'Last checked',
        uptime: 'Uptime',
    },
    adminsPage: {
        title: 'Admins',
        subtitle: 'The single source of truth for admin access. Add or remove the people who can sign in and manage content.',
        email: 'Email',
        name: 'Name',
        note: 'Note',
        addAdmin: 'Add Admin',
        createSuccess: 'Admin added successfully',
        createError: 'Error adding admin',
        updateSuccess: 'Admin updated successfully',
        updateError: 'Error updating admin',
        deleteSuccess: 'Admin removed successfully',
        deleteError: 'Error removing admin',
    },
    messageTemplatesPage: {
        title: 'Message Templates',
        subtitle: 'Edit the message templates used by the mobile app. Use ${} as placeholders for dynamic content.',
        notificationLabel: 'Notification Template',
        notificationHelper: 'Must include at least 1 ${}. Example: "${} is live. Tune in now!"',
        notificationError: 'Must include at least 1 ${} placeholder',
        shareArtistLabel: 'Share Artist Template',
        shareArtistHelper: 'Must include at least 2 ${}. Example: "Check out ${}: ${}"',
        shareArtistError: 'Must include at least 2 ${} placeholders',
        shareRadioLabel: 'Share Radio Template',
        shareRadioHelper: 'Must include at least 2 ${}. Example: "Check out ${}: ${}"',
        shareRadioError: 'Must include at least 2 ${} placeholders',
        shareShowLabel: 'Share Show Template',
        shareShowHelper: 'Must include at least 2 ${}. Example: "Check out ${}: ${}"',
        shareShowError: 'Must include at least 2 ${} placeholders',
        saveButton: 'Save Templates',
        saving: 'Saving...',
        resetButton: 'Reset Changes',
        saveSuccess: 'Templates saved successfully',
        loadError: 'Error loading templates',
        saveError: 'Error saving templates',
        validationError: 'Please fix validation errors before saving',
    },
    lineupGamePage: {
        title: 'Lineup Game',
        subtitle: 'Shows or hides the Lineup game for everyone on the radio website and in the mobile apps. Changes apply within seconds.',
        enabled: 'On: the game is visible to everyone',
        disabled: 'Off: the game is hidden',
        lastChanged: 'Last changed by %{by} on %{at}',
        neverChanged: 'Not changed yet',
        confirmEnableTitle: 'Turn the Lineup game on?',
        confirmEnableContent: 'The Lineup entry will appear for everyone on the website and in the apps.',
        confirmDisableTitle: 'Turn the Lineup game off?',
        confirmDisableContent: 'The Lineup entry will disappear for everyone, and the game will stop accepting plays.',
        enableSuccess: 'Lineup game turned on',
        disableSuccess: 'Lineup game turned off',
        loadError: 'Error loading the Lineup game switch',
        saveError: 'Error updating the Lineup game switch',
    }
};

export default customEnglishMessages;